import { Injectable, EventEmitter } from '@angular/core';
import { ModuleStateService } from '../../module-state.service';
import { SmService } from 'supermappe-core';
import { ApiService } from '../../core/api/api.service';
import { TranslateService } from '@ngx-translate/core';
import { extract } from '../../core/i18n.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Logger } from '../../core/logger.service';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { CreateMapDocMessage, OpenDocMessage } from '../../../../../commondata/messages/messages-dto';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { MessageBoxService } from '../dialog/messagebox.service';
import { StatDto } from '../../core/stats/stat-dto';
import { STATS } from '../../../../../commondata/stats/stats-constants';

const routes = {
    apiDocFindDocFileIdByDocId: '/api/docFindDocFileIdByDocId'
};

const logger: Logger = new Logger('CreateDocService');

@Injectable({
    providedIn: 'root'
})
export class CreateDocService {

    public onDocFileIdChange = new EventEmitter<string>();

    constructor(
        private authenticationService: AuthenticationService,
        private messageBoxService: MessageBoxService,
        private moduleStateService: ModuleStateService,
        private smService: SmService,
        private apiService: ApiService,
        private translateService: TranslateService,
        private http: HttpClient
    ) { }

    private getLocaleStrings(): any {
        let author = '';
        if (this.smService.sm && this.smService.sm.map && this.smService.sm.map.mapProps && this.smService.sm.map.mapProps.author) {
            author = this.smService.sm.map.mapProps.author;
        } else {
            author = 'Anastasis';
        }
        return {
            notes: this.translateService.instant(extract('DOC_NOTES')),
            mapImages: this.translateService.instant(extract('DOC_MAP_IMAGES')),
            author: this.translateService.instant(extract('DOC_AUTHOR')) + ' - ' + author,
            credits: this.translateService.instant(extract('DOC_CREDITS')),
            mathImages: this.translateService.instant(extract('DOC_MATH_IMAGES')),
            mapQRcode: this.translateService.instant(extract('DOC_MAP_QRCODE'))
        };
    }

    public createOrUpdateDocFile(message: string, action: string) {
        return new Observable<any>((observer: any) => {
            if (this.moduleStateService.docFileId) {
                this.apiService.findDocInfoByDocId(this.moduleStateService.docFileId, false, true).subscribe({
                    next: (res: any) => {
                        if (res && res.docInfo && res.docInfo.isTrashed === true) {
                            this.messageBoxService.showTextMessage(
                                this.messageBoxService.MODE_TYPE.YESNO,
                                this.translateService.instant('WARNING'),
                                this.translateService.instant('DOC_TRASHED_WARNING')
                            ).subscribe((result) => {
                                if (result === 'yes') {
                                    this.apiService.trashRecent(this.moduleStateService.docFileId, 'restore').subscribe();
                                    this._createOrUpdateDocFile(observer, message, action);
                                }
                            });
                        } else {
                            this._createOrUpdateDocFile(observer, message, action);
                        }
                    },
                    error: (error: any) => {
                        console.error('findDocInfoByDocId error: ' + error);
                    }
                });
            } else {
                this._createOrUpdateDocFile(observer, message, action);
            }
        });
    }

    private _createOrUpdateDocFile(observer: any, message: string, action: string) {
        this.moduleStateService.setBusyState(true);
        const json = this.smService.getMapDefaultReadOrderInfo();
        json.image = 'document.jpg';
        const locale = this.getLocaleStrings();
        const notes = this.smService.getNotes();
        this.apiService.createNewDocument(this.moduleStateService.name, json, locale, notes, this.moduleStateService.docId).subscribe({
            next: (html: string) => {
                this.apiService.addStatRecords([new StatDto(STATS.MAP.DOCUMENT)]).subscribe();
                const msg = new CreateMapDocMessage(
                    this.moduleStateService.guid,
                    this.moduleStateService.docId,
                    html,
                    this.moduleStateService.name,
                    this.moduleStateService.docFileId,
                    message,
                    action
                );
                (window as any).top.postMessage(msg, "*");
                this.moduleStateService.setBusyState(false);
                observer.next();
                observer.complete();
            },
            error: (error: any) => {
                console.error('createDoc error: ' + error);
                this.moduleStateService.setBusyState(false);
                observer.error();
            }
        });
    }

    public findDocFileIdbyMapId(mapId: string) {
        const body = {
            docId: mapId
        };
        return this.http.post(routes.apiDocFindDocFileIdByDocId, body, {})
            .pipe(
                map(
                    (res: any) => {
                        const id = res.id || '';
                        this.moduleStateService.docFileId = id;
                        this.onDocFileIdChange.emit(id);
                    }
                ),
                catchError((error: any) => {
                    logger.error(error);
                    return observableThrowError(() => error);
                }));
    }

    public openDocFile(docFileId: string) {
        this.apiService.findDocInfoByDocId(docFileId, false, true).subscribe({
            next: (res: any) => {
                if (res && res.docInfo && res.docInfo.isTrashed === true) {
                    this.messageBoxService.showTextMessage(
                        this.messageBoxService.MODE_TYPE.YESNO,
                        this.translateService.instant('WARNING'),
                        this.translateService.instant('TRASH_RESTORE_OPEN_CONFIRM')
                    ).subscribe((result) => {
                        if (result === 'yes') {
                            this.apiService.trashRecent(this.moduleStateService.docFileId, 'restore').subscribe();
                            this._openDocFile(docFileId);
                        }
                    });
                } else {
                    this._openDocFile(docFileId);
                }
            },
            error: (error: any) => {
                console.error('findDocInfoByDocId error: ' + error);
            }
        });
    }

    private _openDocFile(docFileId: string) {
        this.apiService.getShareData(this.authenticationService.getUserEmail(), docFileId).subscribe({
            next: (data: any) => {
                if (data && data.shareData) {
                    console.log('OPEN DOC FILE ID: ' + docFileId);
                    const msg = new OpenDocMessage(this.moduleStateService.guid, docFileId);
                    (window as any).top.postMessage(msg, "*");
                } else {
                    this.messageBoxService.showTextMessage(
                        this.messageBoxService.MODE_TYPE.YESNO,
                        this.translateService.instant('WARNING'),
                        this.translateService.instant('ERROR_MAPDOC_NOT_FOUND')
                    ).subscribe((result) => {
                        if (result === 'yes') {
                            const message = this.translateService.instant('MSG_DOCFILE_CREATED');
                            const action = this.translateService.instant('OPEN');
                            this.createOrUpdateDocFile(message, action).subscribe(() => { });
                        }
                    })
                    console.log('ERROR OPEN DOC FILE ID: ' + docFileId);
                }
            },
            error: (error: any) => {
                this.messageBoxService.showTextMessage(
                    this.messageBoxService.MODE_TYPE.OK,
                    this.translateService.instant('ERROR'),
                    this.translateService.instant('ERROR: ' + error)
                );
                console.error('ERROR getShareData: ' + error);
            }
        })
    }

}
